// 「前回置いた図形の種別」を localStorage に保存する。
// 空白部のダブルクリックと、空白部の右クリックメニューの「図形を配置」が、この値を使って
// 同じ種別の新しい図形を置く。配置前ツールバーの選好（text-size-preference など）と同じ作法で、
// 読み取り・保存とも例外を握って既定値へ落とす。
//
// 種別ごとのサイズと図形の種類まで持つ新しい記憶（placement-size-preference.ts の
// `refinement-canvas:placement-size:v1`）へ移行したあとも、このキーは旧クライアントとの互換と、
// 新しい記憶が壊れたときの種別の拠り所として読み書きを続ける（削除しない）。

const KEY = "refinement-canvas:last-placed-object-kind";

// 1 クリックで完結する配置の種別。ペン（ストロークの形が要る）とコメント（図形ではなく
// 入力ウィンドウを開く操作）は、ダブルクリックで再現できないため含めない。
const PLACEABLE_KINDS = ["sticky", "section", "shape", "text", "table", "stamp"] as const;

export type PlaceableObjectKind = (typeof PLACEABLE_KINDS)[number];

/** 保存値が無い・壊れている・未知の種別のときに使う既定。 */
export const DEFAULT_PLACED_OBJECT_KIND: PlaceableObjectKind = "text";

export function isPlaceableObjectKind(value: unknown): value is PlaceableObjectKind {
  return typeof value === "string" && (PLACEABLE_KINDS as readonly string[]).includes(value);
}

/**
 * 保存されている種別を返す。保存値が無い・壊れている・読めないときは null を返す
 * （既定の種別と「テキストを置いた記録」を区別したい呼び出し側のため）。
 */
export function readStoredLastPlacedObjectKind(): PlaceableObjectKind | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    return isPlaceableObjectKind(raw) ? raw : null;
  } catch {
    // プライベートブラウジングなどで localStorage を読めない環境でも配置はできるようにする。
    return null;
  }
}

export function loadLastPlacedObjectKind(): PlaceableObjectKind {
  return readStoredLastPlacedObjectKind() ?? DEFAULT_PLACED_OBJECT_KIND;
}

/**
 * 前回置いた種別を保存する。保存できなくても配置自体は成功扱いとし、通知もしない。
 * 「利用者が選んだ設定」ではなく「操作の履歴」であり、失敗しても次回が既定へ戻るだけで実害が無いため、
 * text-size-preference のようなロールバック・通知は行わない。
 */
export function saveLastPlacedObjectKind(kind: PlaceableObjectKind): void {
  try {
    window.localStorage.setItem(KEY, kind);
  } catch {
    // 保存できない環境では記録を諦める。
  }
}
